import React, { useRef, useState, useCallback } from 'react';

// Component to display forecast data in a tabular format as requested by the boss
// Data format: [{ forecast_date, target_date, hour, forecast, actuals }]
function ForecastTable({ forecastWindow, loading, dataType, data, targetDate, monthlyHigh, theme }) {
  const isDark = theme === 'dark';
  const scrollRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [showMonthlyTooltip, setShowMonthlyTooltip] = useState(false);
  const longPressTimer = useRef(null);

  // Extract monthly high value and metadata (supports both object and legacy number format)
  const monthlyHighValue = monthlyHigh?.value ?? monthlyHigh ?? 0;
  const monthlyHighDate = monthlyHigh?.date ?? null;
  const monthlyHighHour = monthlyHigh?.hour ?? null;

  // Format the tooltip text for monthly high
  const formatMonthlyHighTooltip = useCallback(() => {
    if (!monthlyHighDate || !monthlyHighHour) return null;
    const date = new Date(monthlyHighDate + 'T00:00:00');
    const monthName = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    return `${monthName} ${day}, Hour ${monthlyHighHour}`;
  }, [monthlyHighDate, monthlyHighHour]);

  // Long press handlers for mobile
  const handleTouchStart = useCallback(() => {
    longPressTimer.current = setTimeout(() => {
      setShowMonthlyTooltip(true);
    }, 500); // 500ms long press
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    // Hide tooltip after a delay on mobile
    setTimeout(() => setShowMonthlyTooltip(false), 2000);
  }, []);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseUp = () => setIsDragging(false);
  const handleMouseLeave = () => setIsDragging(false);

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };
  // Helper function to determine if we should show actuals for a given date
  const shouldShowActuals = (date) => {
    // Use local date to avoid timezone issues
    const now = new Date();
    const localToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      .toISOString().slice(0, 10);
    return date < localToday; // Show actuals ONLY for dates before today (local time)
  };
  
  // Helper function to get actual value for a specific date and hour
  const getActualValue = (date, hour, forecastWindow) => {
    // First check if actuals are in next_sheet_forecasts (for forecastWindow mode)
    if (forecastWindow && forecastWindow.next_sheet_forecasts) {
      const nextSheetData = forecastWindow.next_sheet_forecasts[date];
      if (nextSheetData && nextSheetData[hour]) {
        const actualKey = dataType === 'miso' ? 'MISO_actual' : 'LRZ4_actual';
        if (nextSheetData[hour][actualKey] !== undefined) {
          return nextSheetData[hour][actualKey];
        }
      }
    }
    // Fall back to data prop if available
    if (!data) return null;
    const actualEntry = data.find(d => 
      d.target_date === date && 
      d.hour === parseInt(hour) &&
      d.actuals !== null && d.actuals !== undefined
    );
    return actualEntry ? actualEntry.actuals : null;
  };
  
  // Prefer forecastWindow if available (has 5-day forecast data)
  if (forecastWindow && forecastWindow.forecasts) {
    const forecasts = forecastWindow.forecasts;
    const sortedDates = Object.keys(forecasts).sort();
    
    // Find the highest forecast value across all days (table high)
    let maxVal = null;
    let maxCells = [];

    // Only consider hours 1-24 that are actually rendered in the table
    sortedDates.forEach(date => {
      for (let h = 1; h <= 24; h++) {
        const hour = String(h);
        // Always prefer actual value over forecast for max calculation
        const actualVal = getActualValue(date, hour, forecastWindow);
        let val;
        if (actualVal !== null) {
          val = actualVal;
        } else {
          val = forecasts[date] && forecasts[date][hour] && forecasts[date][hour][dataType.toUpperCase()];
        }

        if (typeof val === 'number' && !isNaN(val)) {
          if (maxVal === null || val > maxVal) {
            maxVal = val;
            maxCells = [[date, hour]];
          } else if (val === maxVal) {
            maxCells.push([date, hour]);
          }
        }
      }
    });

    // Monthly high is ONLY from stored actuals - never override with forecasts
    // storedMonthlyHigh already contains the correct value from the database
    
    // Helper to check if a cell is the max
    function isMaxCell(date, hour) {
      return maxCells.some(([d, h]) => d === date && h === hour);
    }
    
    // Color constants - muted tones that blend well with backgrounds
    const COLORS = isDark ? {
      monthlyHigh: '#1e3a5f',  // Deep muted blue
      tableHigh: '#3d2963',    // Deep muted violet
      within5: '#5c2626',      // Deep muted red
      within10: '#5c4516'      // Deep muted amber
    } : {
      monthlyHigh: '#DBEAFE',  // Soft blue
      tableHigh: '#EDE9FE',    // Soft violet
      within5: '#FEE2E2',      // Soft red/pink
      within10: '#FEF3C7'      // Soft amber/cream
    };

    // Helper to determine cell background color
    function getCellBackground(value, isMax) {
      if (typeof value !== 'number' || isNaN(value)) return undefined;

      // Table high takes TOP priority - this is the value we're watching
      if (isMax) {
        return COLORS.tableHigh;
      }
      // Monthly high (exact match)
      if (monthlyHighValue > 0 && value === monthlyHighValue) {
        return COLORS.monthlyHigh;
      }
      // EXCEEDS monthly high - danger zone!
      if (monthlyHighValue > 0 && value > monthlyHighValue) {
        return COLORS.within5;
      }
      // Within 5% of monthly high
      if (monthlyHighValue > 0 && value >= (monthlyHighValue * 0.95) && value < monthlyHighValue) {
        return COLORS.within5;
      }
      // Within 10% of monthly high
      if (monthlyHighValue > 0 && value >= (monthlyHighValue * 0.90) && value < (monthlyHighValue * 0.95)) {
        return COLORS.within10;
      }

      return undefined;
    }
    
    return (
      <div
        ref={scrollRef}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
        style={{
          overflowX: 'auto',
          overflowY: 'hidden',
          marginBottom: 24,
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: isDragging ? 'none' : 'auto'
        }}
      >
        <h2>5-Day Forecast Table (Sheet Date: {forecastWindow.sheet_date || targetDate})</h2>
        <table style={{ borderCollapse: 'collapse', minWidth: 800, width: '100%' }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid #ccc', padding: '10px 12px' }}>Hour</th>
              {sortedDates.map((date, idx) => {
                // Check if this date has actuals
                let hasActuals = false;
                for (let h = 1; h <= 24; h++) {
                  if (getActualValue(date, String(h), forecastWindow) !== null) {
                    hasActuals = true;
                    break;
                  }
                }
                return (
                  <th key={date} style={{ border: '1px solid #ccc', padding: '10px 12px' }}>
                    {hasActuals ? (
                      <>Actuals<br/><small style={{ fontWeight: 'normal' }}>{date}</small></>
                    ) : (
                      <>DAY {idx + 1}<br/>{date}</>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {[...Array(24)].map((_, hIdx) => {
              const hour = String(hIdx + 1);
              return (
                <tr key={hour}>
                  <td style={{ border: '1px solid #ccc', padding: '10px 12px' }}>{hour}</td>
                  {sortedDates.map(date => {
                    // Check if this date has actuals and use them if so
                    const actualVal = getActualValue(date, hour, forecastWindow);
                    const hasActual = actualVal !== null;
                    const cellVal = hasActual ? actualVal : (forecasts[date][hour] && forecasts[date][hour][dataType.toUpperCase()]);

                    const isMax = isMaxCell(date, hour);
                    const bgColor = getCellBackground(cellVal, isMax);

                    return (
                      <td
                        key={date}
                        style={{
                          border: '1px solid #ccc',
                          padding: '10px 12px',
                          background: bgColor,
                          fontStyle: hasActual ? 'normal' : 'italic'
                        }}
                      >
                        {typeof cellVal !== 'undefined' ? cellVal : '-'}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{ marginTop: 8, fontSize: '0.85em', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span><span style={{ display: 'inline-block', width: '12px', height: '12px', background: COLORS.monthlyHigh, marginRight: '4px', border: isDark ? '1px solid #555' : '1px solid #999' }}></span>Monthly High</span>
            <span><span style={{ display: 'inline-block', width: '12px', height: '12px', background: COLORS.within5, marginRight: '4px', border: isDark ? '1px solid #555' : '1px solid #999' }}></span>&lt;5%</span>
            <span><span style={{ display: 'inline-block', width: '12px', height: '12px', background: COLORS.within10, marginRight: '4px', border: isDark ? '1px solid #555' : '1px solid #999' }}></span>&lt;10%</span>
            <span><span style={{ display: 'inline-block', width: '12px', height: '12px', background: COLORS.tableHigh, marginRight: '4px', border: isDark ? '1px solid #555' : '1px solid #999' }}></span>Table High</span>
          </div>
          <div style={{ display: 'flex', gap: '16px' }}>
            {maxVal > 0 && <span>Table: <b style={{ background: COLORS.tableHigh, padding: '1px 4px', color: isDark ? '#fff' : undefined }}>{Math.round(maxVal)}</b></span>}
            {monthlyHighValue > 0 && (
              <span
                style={{ position: 'relative', cursor: 'pointer' }}
                onMouseEnter={() => setShowMonthlyTooltip(true)}
                onMouseLeave={() => setShowMonthlyTooltip(false)}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
              >
                Month: <b style={{ background: COLORS.monthlyHigh, padding: '1px 4px', color: isDark ? '#fff' : undefined }}>{Math.round(monthlyHighValue)}</b>
                {showMonthlyTooltip && formatMonthlyHighTooltip() && (
                  <span style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: isDark ? '#333' : '#222',
                    color: '#fff',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '0.85em',
                    whiteSpace: 'nowrap',
                    marginBottom: '4px',
                    zIndex: 1000,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                  }}>
                    {formatMonthlyHighTooltip()}
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }
}
export default ForecastTable;
